if (!customElements.get('pt-matching-collection-tabs')) {
  customElements.define('pt-matching-collection-tabs', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.tabs = Array.from(this.querySelectorAll('[data-collection-tab]'));
      this.panel = this.querySelector('[data-tab-panel]');
      this.strip = this.querySelector('[data-tab-strip]');
      this.status = this.querySelector('[data-tab-status]');
      if (!this.tabs.length || !this.panel.querySelector('[data-pt-matching-grid]')) return;
      this.cache = new Map([[this.tabs[0].dataset.handle, this.panel.innerHTML]]);
      this.pending = new Map();
      this.sequence = 0;
      this.activeIndex = 0;
      this.strip.setAttribute('role', 'tablist');
      this.strip.setAttribute('aria-label', 'Matching collections');
      this.panel.setAttribute('role', 'tabpanel');
      this.tabs.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', this.panel.id);
        tab.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
          event.preventDefault();
          this.activate(index);
        });
        // Manual keyboard activation avoids fetching every collection while arrowing through names.
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next !== undefined) {
            event.preventDefault();
            this.tabs[next].focus();
            this.reveal(this.tabs[next]);
          } else if (event.key === ' ') {
            event.preventDefault();
            this.activate(index);
          }
        });
      });
      this.select(0);
      this.arrows = Array.from(this.querySelectorAll('[data-tab-scroll]'));
      this.arrows.forEach(button => button.addEventListener('click', () => {
        this.strip.scrollBy({ left: Number(button.dataset.tabScroll) * this.strip.clientWidth * .7, behavior: this.motion() });
      }));
      this.strip.addEventListener('scroll', () => this.updateArrows(), { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.strip);
      this.updateArrows();
    }
    disconnectedCallback() {
      this.sequence += 1;
      this.resizeObserver?.disconnect();
    }
    motion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }
    reveal(tab) {
      const bounds = this.strip.getBoundingClientRect();
      const target = tab.getBoundingClientRect();
      if (target.left < bounds.left || target.right > bounds.right) {
        this.strip.scrollBy({ left: target.left < bounds.left ? target.left - bounds.left - 4 : target.right - bounds.right + 4, behavior: this.motion() });
      }
    }
    updateArrows() {
      const overflow = this.strip.scrollWidth > this.strip.clientWidth + 2;
      this.arrows.forEach(button => {
        button.hidden = !overflow;
        button.disabled = Number(button.dataset.tabScroll) < 0
          ? this.strip.scrollLeft <= 2
          : this.strip.scrollLeft + this.strip.clientWidth >= this.strip.scrollWidth - 2;
      });
    }
    select(index) {
      this.activeIndex = index;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panel.setAttribute('aria-labelledby', this.tabs[index].id);
    }
    async load(tab) {
      const handle = tab.dataset.handle;
      if (this.cache.has(handle)) return this.cache.get(handle);
      if (!this.pending.has(handle)) {
        const request = (async () => {
          const url = new URL(tab.href, window.location.origin);
          url.searchParams.set('section_id', this.dataset.renderSection);
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection unavailable');
          const document = new DOMParser().parseFromString(await response.text(), 'text/html');
          const grid = document.querySelector('[data-pt-matching-grid]');
          if (!grid || grid.dataset.collectionHandle !== handle) throw new Error('Invalid collection response');
          const html = grid.outerHTML;
          this.cache.set(handle, html);
          return html;
        })();
        this.pending.set(handle, request);
        request.then(() => this.pending.delete(handle), () => this.pending.delete(handle));
      }
      return this.pending.get(handle);
    }
    async activate(index) {
      const sequence = ++this.sequence;
      const tab = this.tabs[index];
      this.reveal(tab);
      this.status.classList.add('visually-hidden');
      this.status.textContent = `Loading ${tab.textContent.trim()}…`;
      this.panel.setAttribute('aria-busy', 'true');
      try {
        const html = await this.load(tab);
        if (sequence !== this.sequence || !this.isConnected) return;
        this.panel.innerHTML = html;
        this.select(index);
        this.status.textContent = `${tab.textContent.trim()} loaded`;
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected) return;
        this.status.classList.remove('visually-hidden');
        this.status.replaceChildren(document.createTextNode('Unable to load this collection. Try the tab again or '));
        const link = document.createElement('a');
        link.href = tab.href;
        link.textContent = `view ${tab.textContent.trim()}`;
        this.status.append(link);
      } finally {
        if (sequence === this.sequence) this.panel.removeAttribute('aria-busy');
      }
    }
  });
}
